import React, { useEffect, useState } from "react";
import { api } from "../api";

interface ScheduleRule {
  day_of_week: string;
  is_active?: boolean;
  expected_start?: string | null;
  expected_end?: string | null;
  notes?: string | null;
  run_rate?: number | null;
  confidence?: number | null;
  sample_size?: number | null;
  is_user_edited?: boolean;
  [extra: string]: any;
}

interface Labelled {
  text: string;
  cls: string;
}

interface ScheduleRulesProps {
  asset: string | null;
}

// Ports app.js's fetchScheduleRules / renderScheduleRulesEditor / saveScheduleRules /
// validateScheduleRules / resetScheduleRules into one self-contained component.
const ScheduleRules: React.FC<ScheduleRulesProps> = ({ asset }) => {
  const [rules, setRules] = useState<ScheduleRule[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<Labelled>({ text: "", cls: "" });
  const [badge, setBadge] = useState<Labelled>({ text: "", cls: "" });

  function load() {
    setLoading(true);
    setLoadError("");
    api.scheduleRules(asset as string).then(({ ok, data }) => {
      if (!ok || !data || data.error) {
        setLoadError("Schedule rules unavailable.");
        setLoading(false);
        return;
      }
      const r: ScheduleRule[] = data.rules || [];
      setRules(r);
      setNotes(r.find((x) => x.notes)?.notes || "");
      setStatus({ text: "", cls: "" });
      const hasUserEdited = r.some((x) => x.is_user_edited);
      const hasML = r.some((x) => x.confidence != null);
      setBadge(
        hasUserEdited
          ? { text: "User-edited", cls: "sr-badge-user" }
          : hasML
          ? { text: "ML-detected", cls: "sr-badge-ml" }
          : { text: "Not computed", cls: "sr-badge-none" }
      );
      setLoading(false);
    });
  }

  useEffect(() => {
    if (!asset) return;
    setRules(null);
    load();
  }, [asset]);

  function updateRule(dow: string, patch: Partial<ScheduleRule>) {
    setRules((prev) => (prev ? prev.map((r) => (r.day_of_week === dow ? { ...r, ...patch } : r)) : prev));
  }

  async function handleSave() {
    if (!rules) return;
    setStatus({ text: "Saving\u2026", cls: "sr-status-info" });
    const payload = rules.map((r) => ({
      day_of_week: r.day_of_week,
      is_active: !!r.is_active,
      expected_start: r.expected_start || null,
      expected_end: r.expected_end || null,
      notes: notes.trim() || null,
    }));
    const { ok, data } = await api.saveScheduleRules({ asset, rules: payload });
    if (!ok || !data || data.error || data.status === 'not_implemented') {
      setStatus({ text: data?.status === 'not_implemented' ? 'Save not available yet' : `Error: ${(data && data.error) || "save failed"}`, cls: "sr-status-err" });
      return;
    }
    setStatus({ text: `Saved ${data.saved} rules.`, cls: "sr-status-ok" });
    setBadge({ text: "User-edited", cls: "sr-badge-user" });
  }

  async function handleValidate() {
    if (!rules) return;
    setStatus({ text: "Validating\u2026", cls: "sr-status-info" });
    // Validate = keep current times but mark days as user-confirmed.
    const payload = rules.map((r) => ({
      day_of_week: r.day_of_week,
      is_active: r.is_active,
      expected_start: r.expected_start,
      expected_end: r.expected_end,
      notes: notes.trim() || r.notes || null,
    }));
    const { ok, data } = await api.saveScheduleRules({ asset, rules: payload });
    if (!ok || !data || data.error || data.status === 'not_implemented') {
      setStatus({ text: data?.status === 'not_implemented' ? 'Save not available yet' : `Error: ${(data && data.error) || "validate failed"}`, cls: "sr-status-err" });
      return;
    }
    setStatus({ text: "ML pattern validated and saved.", cls: "sr-status-ok" });
    setBadge({ text: "User-edited", cls: "sr-badge-user" });
  }

  async function handleReset() {
    if (!window.confirm(`Reset schedule rules for "${asset}" to ML-detected pattern? User edits will be cleared.`)) return;
    setStatus({ text: "Resetting\u2026", cls: "sr-status-info" });
    const { ok, data } = await api.resetScheduleRules(asset as string);
    if (!ok || !data || data.error || data.status === 'not_implemented') {
      setStatus({ text: data?.status === 'not_implemented' ? 'Reset not available yet' : `Error: ${(data && data.error) || "reset failed"}`, cls: "sr-status-err" });
      return;
    }
    setStatus({ text: "Reset to ML. Reloading\u2026", cls: "sr-status-ok" });
    setTimeout(load, 600);
  }

  return (
    <div className="detail-section" id="detail-schedule-rules-section">
      <div className="sr-header-row">
        <h3>
          Schedule Rules
          {badge.text && <span className={badge.cls} id="sr-source-badge" title="Source of the active schedule">{badge.text}</span>}
        </h3>
        <div className="sr-header-actions">
          <button className="sr-btn sr-btn-reset" onClick={handleReset} title="Revert all days to ML-detected pattern">
            &#8635; Reset to ML
          </button>
        </div>
      </div>
      <p className="sr-hint">
        Check the days this asset is expected to run. Uncheck days that are false positives (one-off runs). Save to override ML detection.
      </p>

      {loading && <div id="sr-loading" style={{ color: "#888", fontSize: ".8rem", padding: "8px 0" }}>Loading schedule rules\u2026</div>}
      {!loading && loadError && <div id="sr-loading" style={{ color: "#888", fontSize: ".8rem", padding: "8px 0" }}>{loadError}</div>}

      {!loading && !loadError && rules && (
        <div id="sr-editor">
          <table className="sr-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Active</th>
                <th>Exp Start (UTC)</th>
                <th>Exp End (UTC)</th>
                <th>Run Rate</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const rate = r.run_rate != null ? Math.round(r.run_rate * 100) : null;
                const conf = r.confidence != null ? Math.round(r.confidence * 100) : null;
                const confColor = conf == null ? "#888" : conf >= 80 ? "#1e8e3e" : conf >= 55 ? "#f79227" : "#b91c1c";
                return (
                  <tr key={r.day_of_week} className={`sr-row${r.is_active ? "" : " sr-row-inactive"}`}>
                    <td className="sr-day">{r.day_of_week}</td>
                    <td className="sr-active-cell">
                      <label className="sr-toggle">
                        <input
                          type="checkbox"
                          className="sr-chk"
                          checked={!!r.is_active}
                          onChange={(e) => updateRule(r.day_of_week, { is_active: e.target.checked })}
                        />
                        <span className="sr-toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="time"
                        className="sr-time sr-start"
                        value={r.expected_start || ""}
                        onChange={(e) => updateRule(r.day_of_week, { expected_start: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="sr-time sr-end"
                        value={r.expected_end || ""}
                        onChange={(e) => updateRule(r.day_of_week, { expected_end: e.target.value })}
                      />
                    </td>
                    <td className="sr-rate">
                      {rate != null ? `${rate}%` : "\u2014"}
                      {rate != null && (
                        <div className="sr-rate-bar">
                          <div className="sr-rate-fill" style={{ width: `${Math.min(rate, 100)}%`, background: confColor }}></div>
                        </div>
                      )}
                    </td>
                    <td className="sr-conf" style={{ color: confColor }}>
                      {conf != null ? `${conf}%` : r.sample_size ? "Low" : "\u2014"}
                      {r.sample_size ? <div className="sr-sample">n={r.sample_size}</div> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="sr-notes-row">
            <label htmlFor="sr-notes">Notes</label>
            <textarea
              id="sr-notes"
              className="sr-notes"
              rows={2}
              placeholder="Optional note about this schedule (e.g. 'Mon\u2013Fri only per data team')"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="sr-action-row">
            <button className="sr-btn sr-btn-validate" onClick={handleValidate} title="Keep ML-detected times but mark as user-confirmed">
              &#10003; Validate (keep ML)
            </button>
            <button className="sr-btn sr-btn-save" onClick={handleSave}>&#128190; Save Changes</button>
          </div>

          {status.text && <div id="sr-status" className={`sr-status ${status.cls}`}>{status.text}</div>}
        </div>
      )}
    </div>
  );
};

export default ScheduleRules;
